import { useCallback, useMemo, useRef, useState } from 'react'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'
import { usePropertiesPanelSections } from '@/hooks/use-properties-panel-sections'
import { usePropertiesPanelScroll } from '@/hooks/use-properties-panel-scroll'
import type { ActiveSelection } from '@/components/app/editor/active-object-context'
import { computePropertiesPanelSelectionSignature } from '@/lib/panels/properties-panel-default-section'
import { useSectionElementRegistry } from './use-section-element-registry'

interface UseSelectToolSectionsInput {
  activeItem: string
  /** `ActiveObjectContext` (already the source of truth for selection elsewhere in the panel). */
  activeObjectCtx: ActiveSelection | null
}

/**
 * The Select tool's Properties Panel sections: the real open-section state, the ephemeral
 * no-selection layer, the scroll-to-section wiring, and the user-facing open/collapse actions.
 */
export function useSelectToolSections({ activeItem, activeObjectCtx }: UseSelectToolSectionsInput) {
  const {
    openSectionIds: selectToolOpenSectionIds,
    openSection: openSelectToolSectionRaw,
    setOpenSectionIds: setSelectToolOpenSectionIdsRaw,
    collapseAll: collapseAllSelectToolSections,
    pinnedSectionId: selectToolPinnedSectionId,
  } = usePropertiesPanelSections()

  const { scrollContainerRef, registerSectionElement, getSectionElement } =
    useSectionElementRegistry()

  // Owned by `LeftPanel` (which persists for the life of the editor session) rather than inside
  // `SelectTool` itself, which unmounts whenever the user switches to another tool tab — both the
  // auto-open hook and the scroll hook need this selection identity to survive that unmount.
  const selectToolSelectionSignature = useMemo(
    () =>
      computePropertiesPanelSelectionSignature({
        shapes: activeObjectCtx?.activeObjects ?? [],
        textBoxes: activeObjectCtx?.activeTextBoxes ?? [],
      }),
    [activeObjectCtx?.activeObjects, activeObjectCtx?.activeTextBoxes]
  )
  const hasSelection = selectToolSelectionSignature.key != null

  // No-selection display collapse: while nothing is selected, all five sections render
  // collapsed — a purely visual state, never a mutation of the real persistent/selection-scoped
  // section state above (`selectToolOpenSectionIds`/`usePropertiesPanelSections`). Any section
  // the user opens while there's no selection lives only in this separate, ephemeral slice, so
  // it can never leak into — or overwrite — a real object's saved section state. Reselecting
  // simply switches back to displaying the untouched real state.
  const [noSelectionOpenSectionIds, setNoSelectionOpenSectionIds] = useState<
    PropertiesPanelSectionId[]
  >([])
  const hadSelectionRef = useRef(hasSelection)

  // Entering a fresh no-selection stretch always starts fully collapsed, regardless of what was
  // left open during a *previous* no-selection stretch — that state was always temporary. Reset
  // during render (React's supported "adjust state when a dependency changes" pattern) rather
  // than in an effect, so the collapse is never delayed by an extra commit and the previous
  // stretch's leftover open ids are never even painted.
  if (hadSelectionRef.current !== hasSelection) {
    hadSelectionRef.current = hasSelection

    if (!hasSelection && noSelectionOpenSectionIds.length > 0) {
      setNoSelectionOpenSectionIds([])
    }
  }

  // What the Accordion actually renders as open: the real state while something is selected,
  // the ephemeral no-selection state otherwise.
  const displayedSelectToolOpenSectionIds = hasSelection
    ? selectToolOpenSectionIds
    : noSelectionOpenSectionIds

  const { notifySectionOpenedByUser, notifySectionClosedByUser } = usePropertiesPanelScroll({
    isActive: activeItem === 'select',
    scrollContainerRef,
    getSectionElement,
    openSectionIds: displayedSelectToolOpenSectionIds,
    pinnedSectionId: selectToolPinnedSectionId,
    selectionKey: selectToolSelectionSignature.key,
    selectionDefaultSectionId: selectToolSelectionSignature.defaultSectionId,
  })

  // The user's own latest open/close of each section (header click, shortcut, nav icon).
  // Auto-open never reopens a section the user last collapsed (see
  // `usePropertiesPanelAutoOpenSection`), so e.g. collapsing Object keeps it collapsed for the
  // next object until the user opens it again. Not state: it never affects what renders now.
  const explicitSectionStateRef = useRef(new Map<PropertiesPanelSectionId, boolean>())
  const getExplicitSectionState = useCallback(
    (id: PropertiesPanelSectionId) => explicitSectionStateRef.current.get(id),
    []
  )

  // Wraps the raw opener so every direct "the user opened a section" action (keyboard
  // shortcuts, nav-icon click, an imperative expand/open, and the Accordion header click
  // handled by `handleOpenSectionIdsChange` below) also scrolls that section to the panel's top
  // — the "OPENING A SECTION" rule. Selection-driven auto-open
  // (`usePropertiesPanelAutoOpenSection` in `LeftPanel`) deliberately calls the raw setter
  // directly instead, so the scroll follows the selection's own section. An explicit open only
  // ever lifts a collapse, so it's recorded with or without a selection — which also keeps this
  // callback's identity stable across selection changes (`useToolShortcuts` subscribes with it).
  const openSelectToolSection = useCallback(
    (id: PropertiesPanelSectionId) => {
      openSelectToolSectionRaw(id)
      notifySectionOpenedByUser(id)
      explicitSectionStateRef.current.set(id, true)
    },
    [openSelectToolSectionRaw, notifySectionOpenedByUser]
  )

  // "Collapse All" is an explicit user action, distinct from the no-selection display collapse
  // (see the "no selection" panel spec) — but its result must look the same regardless of which
  // layer happens to be displayed, so it clears both: the real section state, and whatever was
  // temporarily opened during the current no-selection stretch. As a reset, it also forgets the
  // explicit per-section choices, so auto-open works as new.
  const collapsePropertiesPanelAllSections = useCallback(() => {
    collapseAllSelectToolSections()
    setNoSelectionOpenSectionIds([])
    explicitSectionStateRef.current.clear()
  }, [collapseAllSelectToolSections])

  // A temporary collapse (opening a right panel), distinct from "Collapse All": only the
  // no-selection display layer is reset. A selected object keeps showing its own sections, and
  // each selection's saved state is left untouched (WMPCM-11615 AC 3, see `LeftPanelRef`'s
  // `handleRightPanelOpened`).
  const collapsePropertiesPanelSectionsTemporarily = useCallback(() => {
    setNoSelectionOpenSectionIds([])
  }, [])

  const handleOpenSectionIdsChange = (next: string[]) => {
    // Expanding/collapsing an accordion section is a pure UI toggle — it must not
    // switch tools or enter live text-edit mode. The Select tool's own Type section
    // already lets the user restyle a merely-selected text frame's font without an
    // active editing session (`applyShapeTextRunStyle`'s non-editing branch in
    // `shape-manager/hooks/use-shape-text-editing.ts`); forcing edit mode here made simply opening the section
    // hijack the canvas into the Type tool with a live caret. Switching to the Type
    // tool via the rail button is a separate, correct path — `handleActivateTool`'s
    // own 'type' branch (`home.tsx`) — and is untouched by this.
    //
    // A header click here is a direct user action — scroll whichever section(s) just
    // opened to the top, same as any other manual open (see `openSelectToolSection`).
    // A header click that collapses a section brings the section right below it up, rather
    // than whatever a Layout/Effects pin elsewhere would pick (see `notifySectionClosedByUser`).
    // Only a toggle on a real selection is remembered as the user's choice; with nothing
    // selected it's temporary (see below).
    for (const id of next as PropertiesPanelSectionId[]) {
      if (!displayedSelectToolOpenSectionIds.includes(id)) {
        notifySectionOpenedByUser(id)
        if (hasSelection) explicitSectionStateRef.current.set(id, true)
      }
    }

    for (const id of displayedSelectToolOpenSectionIds) {
      if (!next.includes(id)) {
        notifySectionClosedByUser(id)
        if (hasSelection) explicitSectionStateRef.current.set(id, false)
      }
    }

    // With no selection, a header click is a purely temporary/display toggle — route
    // it to the ephemeral no-selection slice instead of the real section state so it
    // can never overwrite any object's saved open/closed state (see the "no selection"
    // panel spec's "temporary section open" rule).
    if (hasSelection) {
      setSelectToolOpenSectionIdsRaw(next)
    } else {
      setNoSelectionOpenSectionIds(next as PropertiesPanelSectionId[])
    }
  }

  return {
    selectToolOpenSectionIds,
    openSelectToolSectionRaw,
    setSelectToolOpenSectionIdsRaw,
    selectToolSelectionSignature,
    displayedSelectToolOpenSectionIds,
    openSelectToolSection,
    collapsePropertiesPanelAllSections,
    collapsePropertiesPanelSectionsTemporarily,
    getExplicitSectionState,
    handleOpenSectionIdsChange,
    scrollContainerRef,
    registerSectionElement,
  }
}
